import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { MessageCircle, X, Send, User, ArrowUp, Bot } from 'lucide-react'
import type { ChatMessage, Product } from '../../../lib/types'
import { useAuth } from '../../auth/hooks/useAuth'
import { sendTelegramNotification } from '../../../lib/telegram'
import { useMobileKeyboardFix } from '../../../hooks/useMobileKeyboardFix'
import { apiGetJson, apiPostJson } from '../../../lib/apiClient'
import AuthModal from '../../auth/components/AuthModal'
import { subscribe, subscribeStatus } from '../../realtime/userEventsSocket'
import { unwrapList } from '../../../lib/apiUnwrap'
import { useToast } from '../../../lib/toast'

type AiMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
}

const PRESET_QUESTIONS = [
  'DigiGO đang có những sản phẩm nào nổi bật?',
  'Gợi ý cho mình gói phù hợp nhu cầu học tập/làm việc.',
  'Sản phẩm nào phù hợp để lập trình? Có gói 30 ngày/90 ngày không?',
  'Cho mình so sánh các gói theo thời hạn và giá.',
  'Mình muốn mua: hướng dẫn các bước thanh toán giúp mình.',
  'Sau khi mua thì nhận tài khoản/key ở đâu và cách sử dụng như thế nào?',
  'Có ưu đãi/giảm giá nào hiện tại không?',
  'Nếu gặp lỗi đăng nhập hoặc không dùng được thì xử lý thế nào?'
]

const buildProductContext = (products: Product[]) => {
  const lines: string[] = []

  for (const p of products) {
    if (!p || (p as any).is_paused) continue

    const variants = (Array.isArray((p as any).variants) ? (p as any).variants : []).filter((v: any) => !(v as any).is_archived && !(v as any).is_paused)
    const variantText = variants
      .slice(0, 6)
      .map((v: any) => {
        const price = typeof (v as any).price === 'number' ? (v as any).price.toLocaleString('vi-VN') : String((v as any).price || '')
        const duration = (v as any).duration_days ? `, ${(v as any).duration_days} ngày` : ''
        return `- ${(v as any).name}: ${price}đ${duration}`
      })
      .join('\n')

    const header = `Sản phẩm: ${(p as any).name}${(p as any).category ? ` (Danh mục: ${(p as any).category})` : ''}`
    lines.push(header)
    if (variantText.trim()) lines.push(variantText)

    if ((p as any).product_code) lines.push(`Mã: ${(p as any).product_code}`)
    if ((p as any).mechanism) lines.push(`Cơ chế: ${(p as any).mechanism}`)
    if ((p as any).recommended_model) lines.push(`Model đề xuất: ${(p as any).recommended_model}`)

    lines.push('')

    if (lines.join('\n').length > 6000) break
  }

  return lines.join('\n').trim()
}

export default function ChatWidget() {
  useMobileKeyboardFix() // Activate keyboard fix hook
  const { user } = useAuth()
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<'support' | 'ai'>('support')

  const [supportMessages, setSupportMessages] = useState<ChatMessage[]>([])
  const [supportInput, setSupportInput] = useState('')
  const [supportLoading, setSupportLoading] = useState(false)

  const [aiMessages, setAiMessages] = useState<AiMessage[]>([])
  const [aiInput, setAiInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])

  const [showAuthModal, setShowAuthModal] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [wsConnected, setWsConnected] = useState(false)
  const isOpenRef = useRef(false)
  const knownMessageIdsRef = useRef<Set<string>>(new Set())

  const productContext = useMemo(() => buildProductContext(products), [products])

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  const formatDateTimeVN = (date: Date) => {
    const parts = new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date)

    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value || ''
    return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`
  }

  const fetchMessages = useCallback(async () => {
    if (!user) return

    try {
      const resp = await apiGetJson<any>('/api/chat/messages')
      const newMessages = unwrapList<ChatMessage>(resp)
      newMessages.forEach((m) => {
        if (m?.id) knownMessageIdsRef.current.add(String(m.id))
      })
      setSupportMessages(prev => {
        if (
          prev.length === newMessages.length &&
          prev.length > 0 &&
          prev[prev.length - 1].id === newMessages[newMessages.length - 1].id
        ) {
          return prev
        }
        return newMessages
      })
    } catch (error) {
      console.error('Error fetching messages:', error)
    }
  }, [user])

  useEffect(() => {
    if (!user) {
      setWsConnected(false)
      knownMessageIdsRef.current.clear()
      return
    }

    const unsubStatus = subscribeStatus((c) => setWsConnected(c))
    const unsubChat = subscribe('chat_message', (data) => {
      const row = data
      if (row.user_id !== user.id) return

      const rowId = String(row.id || '').trim()
      if (!rowId) return
      if (knownMessageIdsRef.current.has(rowId)) return
      knownMessageIdsRef.current.add(rowId)

      setSupportMessages((prev) => {
        const next = [...prev, row]
        next.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        return next
      })

      const isClosed = !isOpenRef.current
      const isIncomingAdmin = Boolean(row.is_admin)
      if (isClosed && isIncomingAdmin) {
        setUnreadCount((c) => Math.min(99, c + 1))
        toast({
          variant: 'info',
          title: 'Tin nhắn mới từ hỗ trợ',
          message: String(row.message || 'Bạn có tin nhắn mới từ support.'),
          durationMs: 5000,
          saveToNotifications: true,
        })
        if (audioRef.current) {
          try {
            audioRef.current.currentTime = 0
            void audioRef.current.play()
          } catch {
            // ignore
          }
        }
      }
    })

    return () => {
      unsubChat()
      unsubStatus()
      setWsConnected(false)
    }
  }, [user, toast])

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined

    if (user && isOpen && mode === 'support') {
      fetchMessages()
      if (!wsConnected) {
        interval = setInterval(fetchMessages, 1000)
      }
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [user, isOpen, mode, fetchMessages, wsConnected])

  useEffect(() => {
    scrollToBottom()
  }, [mode, supportMessages, aiMessages, aiLoading])

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const resp = await apiGetJson<any>('/api/products')
        setProducts(unwrapList<Product>(resp))
      } catch {
        setProducts([])
      }
    }

    if (!isOpen || mode !== 'ai' || products.length > 0) return
    void fetchProducts()
  }, [isOpen, mode, products.length])

  useEffect(() => {
    // Create notification sound
    audioRef.current = new Audio('/notification.mp3')
    audioRef.current.volume = 0.7
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const sendText = useCallback(async (text: string) => {
    if (!text.trim() || supportLoading) return

    if (!user) {
      setSupportInput(text) // Preserve message
      setShowAuthModal(true)
      return
    }

    setSupportLoading(true)
    try {
      await apiPostJson<any>('/api/chat/send', { message: text.trim() })

      // Send Telegram notification if user is not admin
      if (!user.is_admin) {
        const fullName = user.full_name || user.email || 'N/A'
        const username = user.username || 'N/A'
        const timeStr = formatDateTimeVN(new Date())
        const telegramMsg = `<b>💬 Tin nhắn mới từ khách hàng:</b>\n\n- Thời gian: ${timeStr}\n- Họ tên: ${fullName}\n- Username: ${username}\n- Nội dung: ${text.trim()}\n\n#UID:${user.id}`
        sendTelegramNotification(telegramMsg, { username, full_name: fullName })
      }

      setSupportInput('')
    } catch (error) {
      console.error('Error sending message:', error)
    } finally {
      setSupportLoading(false)
    }
  }, [supportLoading, user])

  // Listen for open-chat-support event
  useEffect(() => {
    const handleOpenChat = async (e: any) => {
      setIsOpen(true)
      setMode('support')
      if (e.detail?.message) {
        const autoReply: string | undefined = typeof e.detail?.autoReply === 'string' ? e.detail.autoReply : undefined
        // Auto send if logged in, otherwise pre-fill
        if (user) {
          await sendText(e.detail.message)

          // Check if message is a manual delivery order request
          // "Tôi vừa mua đơn hàng: ..."
          if (e.detail.message.startsWith('Tôi vừa mua đơn hàng:')) {
            // Send auto reply after 1 second
            setTimeout(async () => {
              try {
                void autoReply
                await apiPostJson<any>('/api/chat/auto-reply', { trigger: 'manual_delivery_order' })
              } catch (error) {
                console.error('Error sending auto reply:', error)
              }
            }, 1000)
          }
        } else {
          setSupportInput(e.detail.message)
          setShowAuthModal(true)
        }
      }
    }

    window.addEventListener('open-chat-support', handleOpenChat)
    return () => window.removeEventListener('open-chat-support', handleOpenChat)
  }, [user, sendText])

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!supportInput.trim() || supportLoading) return

    if (!user) {
      setShowAuthModal(true)
      return
    }

    setSupportLoading(true)
    try {
      await apiPostJson<any>('/api/chat/send', { message: supportInput.trim() })

      // Send Telegram notification if user is not admin
      if (!user.is_admin) {
        const username = user.username || 'N/A'
        const fullName = user.full_name || 'N/A'

        const timeStr = formatDateTimeVN(new Date())
        const telegramMsg = `<b>💬 Tin nhắn mới từ khách hàng:</b>\n\n- Thời gian: ${timeStr}\n- Họ tên: ${fullName}\n- Username: ${username}\n- Nội dung: ${supportInput.trim()}\n\n#UID:${user.id}`
        sendTelegramNotification(telegramMsg, { username, full_name: fullName })
      }

      setSupportInput('')
    } catch (error) {
      console.error('Error sending message:', error)
    } finally {
      setSupportLoading(false)
    }
  }

  const sendAi = useCallback(async () => {
    const text = aiInput.trim()
    if (!text || aiLoading) return

    const userMsg: AiMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: text,
    }

    const nextMessages = [...aiMessages, userMsg]

    setAiMessages(nextMessages)
    setAiInput('')
    setAiLoading(true)

    try {
      const payload = {
        messages: nextMessages.slice(-12).map(m => ({ role: m.role, content: m.content })),
        product_context: productContext,
      }

      const data: any = await apiPostJson<any>('/api/ai-chat', payload)
      const replyText = String(data?.reply || data?.data?.reply || '').trim()
      if (!replyText) throw new Error('AI trả về nội dung rỗng')

      const botMsg: AiMessage = {
        id: `${Date.now()}-assistant`,
        role: 'assistant',
        content: replyText,
      }

      setAiMessages(prev => [...prev, botMsg])
    } catch (e: any) {
      const botMsg: AiMessage = {
        id: `${Date.now()}-assistant-error`,
        role: 'assistant',
        content: `Lỗi: ${e?.message || 'Không xác định'}`,
      }
      setAiMessages(prev => [...prev, botMsg])
    } finally {
      setAiLoading(false)
    }
  }, [aiInput, aiLoading, aiMessages, productContext])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleToggle = () => {
    // Unlock notification sound on first user interaction (autoplay policy)
    if (!isOpen && audioRef.current) {
      audioRef.current.currentTime = 0
      audioRef.current.play().then(() => {
        audioRef.current?.pause()
        if (audioRef.current) audioRef.current.currentTime = 0
      }).catch(() => { })
    }

    setIsOpen(!isOpen)
    if (!isOpen) {
      setUnreadCount(0) // Reset unread when opening chat
    }
  }

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      {/* Chat Buttons */}
      <div className="fixed bottom-16 lg:bottom-6 right-6 z-40 flex flex-col items-end gap-3">
        {showScrollTop && !isOpen && (
          <button
            onClick={handleScrollToTop}
            aria-label="Lên đầu trang"
            className="bg-gray-900 text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowUp className="h-6 w-6" />
          </button>
        )}
        <div className="relative">
          <button
            onClick={handleToggle}
            className="bg-amber-600 text-white p-3 rounded-full shadow-lg hover:bg-amber-700 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
          </button>
          {unreadCount > 0 && !isOpen && (
            <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed right-6 bottom-[7.5rem] lg:bottom-20 w-80 bg-white rounded-lg shadow-xl border z-40 flex flex-col"
          style={{
            height: '24rem', // 96 = 24rem
          }}
        >
          {/* Header */}
          <div className="bg-amber-600 text-white p-4 rounded-t-lg flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{mode === 'support' ? 'Hỗ trợ khách hàng' : 'Trò chuyện với AI'}</h3>
              <p className="text-sm opacity-90 truncate">
                {mode === 'support'
                  ? (user ? 'Chúng tôi sẽ phản hồi sớm nhất có thể' : 'Vui lòng đăng nhập để chat')
                  : 'Không lưu lịch sử'}
              </p>
            </div>

            <div className="flex items-center bg-amber-700/60 rounded-md p-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => setMode('support')}
                className={`px-2 py-1 rounded text-xs transition-colors ${mode === 'support' ? 'bg-white text-amber-700' : 'text-white/90 hover:bg-amber-700'}`}
              >
                Hỗ trợ
              </button>
              <button
                type="button"
                onClick={() => setMode('ai')}
                className={`ml-1 px-2 py-1 rounded text-xs transition-colors inline-flex items-center gap-1 ${mode === 'ai' ? 'bg-white text-amber-700' : 'text-white/90 hover:bg-amber-700'}`}
              >
                <Bot className="h-3.5 w-3.5" />
                AI
              </button>
            </div>
          </div>

          {mode === 'support' ? (
            user ? (
              <>
                {/* Messages */}
                <div className="flex-1 p-4 overflow-y-auto space-y-3">
                  {supportMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.is_admin ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-xs px-3 py-2 rounded-lg ${message.is_admin
                          ? 'bg-blue-600 text-white'
                          : 'bg-amber-600 text-white'
                          }`}
                      >
                        <p className="text-sm">{message.message}</p>
                        <p className={`text-xs mt-1 ${message.is_admin ? 'text-blue-100' : 'text-amber-100'
                          }`}>
                          {formatDateTimeVN(new Date(message.created_at))}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendMessage} className="p-4 border-t">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={supportInput}
                      onChange={(e) => setSupportInput(e.target.value)}
                      placeholder="Nhập Tin nhắn..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 text-sm"
                    />
                    <button
                      type="submit"
                      disabled={supportLoading || !supportInput.trim()}
                      className="bg-amber-600 text-white p-2 rounded-md hover:bg-amber-700 disabled:opacity-50 transition-colors"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-center">
                  <User className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Vui lòng đăng nhập để sử dụng chat</p>
                </div>
              </div>
            )
          ) : (
            <>
              <div className="flex-1 p-4 overflow-y-auto space-y-3">
                {aiMessages.length === 0 && !aiLoading && (
                  <div className="text-sm text-gray-600">
                    Bạn có thể hỏi về sản phẩm, giá, gói, thời hạn, cách mua... (AI sẽ không lưu lịch sử).
                  </div>
                )}

                {aiMessages.length === 0 && !aiLoading && (
                  <div className="mt-2">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Câu hỏi gợi ý</div>
                    <div className="flex flex-wrap gap-2">
                      {PRESET_QUESTIONS.slice(0, 6).map((q) => (
                        <button
                          key={q}
                          type="button"
                          onClick={() => setAiInput(q)}
                          className="text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {aiMessages.map(m => (
                  <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${m.role === 'user'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                        }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}

                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] px-3 py-2 rounded-lg text-sm bg-gray-100 text-gray-700">
                      Đang trả lời...
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void sendAi()
                }}
                className="p-4 border-t"
              >
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    placeholder="Nhập câu hỏi..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={aiLoading || !aiInput.trim()}
                    className="bg-amber-600 text-white p-2 rounded-md hover:bg-amber-700 disabled:opacity-50 transition-colors"
                    aria-label="Gửi"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  )
}
